/**
 * Core auth/route helpers. Login and token exchange are handled by MSAL (see auth/).
 * See docs/AUTH_UPGRADE_README.md Part F.2.
 */

import { authConfig } from 'src/config';
import { getMsalInstance, loginRequestScopes } from 'src/auth/msalConfig';
import { storeInfoBeforeRedirect, cleanupAfterComingBack, setRoute } from 'src/app/functions/storage';
import { ONE_HOUR } from 'src/constants';

export function generateKey(): string {
  const array = new Uint32Array(28);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
}

export const getUrlDetails = (): { siteName: null | string; pageRoute: string } => {
  const [, site, route] = window.location.pathname.split('/');
  const validate = (value: string | undefined) =>
    value !== undefined && value && value !== 'null' ? value : null;
  return {
    siteName: validate(site),
    pageRoute: validate(route) || '',
  };
};

export const getCurrentRoute = (): string => {
  const { pathname, search } = window.location;
  return `${pathname}${search}`;
};

export const getIdleTimeout = (): number => {
  const timeoutDuration = Number(window.localStorage.getItem('timeoutDuration'));
  if (timeoutDuration && !isNaN(timeoutDuration)) {
    console.log(`temporary idle timeout: ${timeoutDuration / 60 / 1000} Min`);
    return Number(timeoutDuration);
  }
  const { idleSessionTimeout } = authConfig;
  return idleSessionTimeout ? Math.max(idleSessionTimeout, ONE_HOUR) : ONE_HOUR;
};

export const setAppUri = (siteName: string): void => {
  cleanupAfterComingBack();
  window.history.replaceState({}, '', `/${siteName}`);
};

export const endpoint = (type: string): string => {
  const { instance, tenant, signInPolicy } = authConfig;
  switch (type) {
    case 'authorize':
      return `${instance}/${tenant}/${signInPolicy}/oauth2/v2.0/authorize?`;
    case 'token':
      return `${instance}/${tenant}/${signInPolicy}/oauth2/v2.0/token`;
    case 'signout':
      return `${instance}/${tenant}/${signInPolicy}/oauth2/v2.0/logout?`;
    default:
      return '';
  }
};

export const parseQueryString = (str: string): Record<string, string> => {
  const queryString: Record<string, string> = {};
  if (str === '') {return queryString;}
  const segments = str.split('&').map(s => s.split('='));
  segments.forEach(s => (queryString[s[0]] = s[1]));
  return queryString;
};

export const signOut = (code = '000'): void => {
  const { siteName } = getUrlDetails();
  const state = `${siteName}|${code}|${generateKey()}`;
  storeInfoBeforeRedirect({ state });

  if (siteName && code === '401') {
    setRoute(getCurrentRoute());
    getMsalInstance().loginRedirect({ scopes: loginRequestScopes });
  } else {
    const { redirectUri } = authConfig;
    const postLogoutUri =
      siteName && siteName !== 'logout'
        ? `${redirectUri}/${siteName}/logout`
        : `${redirectUri}/logout`;
    const logoutEndpoint = `${endpoint('signout')}post_logout_redirect_uri=${encodeURI(
      postLogoutUri
    )}&state=${state}`;
    window.location.href = logoutEndpoint;
  }
};
